import { useMemo } from 'react';
import { State } from '@progress/kendo-data-query';
import { PageChangeEvent, Pager } from '@progress/kendo-react-data-tools';

import {
  DefaultGridSettings,
  DefaultPagerSettings
} from '../constants/grid-settings';
import { ZoneLibraries } from '../types/responses/zone-library-response';
import PopupMenu, { popupMenu as MenuList } from '../components/popupMenu';

export interface CardViewDataStateChangeEvent {
  dataState: State;
}

export interface ZoneCardViewProps extends State {
  total?: number;
  isAdmin?: boolean;
  isGlobal?: boolean;
  data?: ZoneLibraries[];
  onCopy?: (id: number) => void;
  onExport: (id: number) => void;
  onDelete: (id: number) => void;
  onPreview: (id: number) => void;
  onCopyToGame?: (id: number) => void;
  onTitlelick?: (id: number) => void;
  onDataStateChange?: (event: CardViewDataStateChangeEvent) => void;
}

export const ZoneCardView = ({
  data,
  isAdmin,
  isGlobal,
  total = 0,
  onCopy,
  onDelete,
  onExport,
  onPreview,
  onTitlelick,
  onCopyToGame,
  ...props
}: ZoneCardViewProps) => {
  const handleTitleClick = (id: number) => {
    if (onTitlelick) {
      onTitlelick(id);
    }
  };

  const menuList: MenuList[] = useMemo(
    () => [
      { classIcon: 'visibility', textMenu: 'Preview', textClass: '' },
      ...(isGlobal
        ? [
            ...(isAdmin
              ? [{ classIcon: 'delete', textMenu: 'Delete', textClass: '' }]
              : [
                  {
                    classIcon: 'library_add_check',
                    textMenu: 'Copy to My Zone Map Library',
                    textClass: ''
                  }
                ])
          ]
        : [
            {
              classIcon: 'download_for_offline',
              textMenu: 'Export to JSON',
              textClass: ''
            },
            {
              classIcon: 'library_add_check',
              textMenu: 'Copy Zone Map to Game',
              textClass: ''
            },
            { classIcon: 'delete', textMenu: 'Delete', textClass: '' }
          ])
    ],
    [isGlobal]
  );

  const onMenuSelected = (id: number, menu: MenuList) => {
    if (!id) return;

    switch (menu.textMenu) {
      case 'Preview':
        onPreview(id);
        break;
      case 'Export to JSON':
        onExport(id);
        break;
      case 'Copy Zone Map to Game':
        onCopyToGame?.(id);
        break;
      case 'Delete':
        onDelete(id);
        break;
      case 'Copy to My Zone Map Library':
        onCopy?.(id);
        break;
    }
  };

  const handlePageChange = (event: PageChangeEvent) => {
    raiseDataStateChangeEvent({ skip: event.skip, take: event.take });
  };

  /**
   * Raise the Grid DataState change event. I.e. filters have changed.
   * @param moreData
   */
  const raiseDataStateChangeEvent = (moreData: State) => {
    props.onDataStateChange?.({
      dataState: { ...getDataState(), ...moreData }
    });
  };

  const getDataState = () => {
    return {
      filter: props.filter,
      sort: props.sort,
      skip: props.skip,
      take:
        props.take !== undefined ? props.take : DefaultGridSettings.pageSize,
      group: props.group
    };
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
        overflow: 'hidden'
      }}>
      <div
        className={'row grid-card-library'}
        style={{
          flexGrow: 1,
          overflow: 'auto'
        }}>
        {data &&
          data.map((zone) => {
            return (
              <div
                className={'col-xs-12 col-sm-6 col-md-4 d-flex'}
                key={zone.id}>
                <div className={'card mt-4 card-view card-library flex-grow-1'}>
                  {zone.previewImageUrl ? (
                    <img
                      src={zone.previewImageUrl}
                      width={'100%'}
                      height={180}
                      style={{
                        objectFit: 'cover'
                      }}
                      alt={'zone-preview'}
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '180px',
                        backgroundColor: '#d0d0d0',
                        borderRadius: '4px 4px 0 0'
                      }}
                    />
                  )}

                  <div className={'card-body pt-2 pb-2 pl-3 pr-3'}>
                    <div
                      className={
                        'd-flex justify-content-between align-items-center'
                      }>
                      <h3
                        className={'title link-primary cursor-pointer mb-0'}
                        onClick={() => handleTitleClick(zone.id)}>
                        {zone.name}
                      </h3>
                      <PopupMenu
                        id={zone.id}
                        menus={menuList}
                        onMenuSelected={onMenuSelected}
                      />
                    </div>

                    <hr className={'mt-1 mb-2'} />

                    <p className={'mb-1'} style={{ fontSize: '0.875rem' }}>
                      {zone.description}
                    </p>

                    <div style={{ fontSize: '0.875rem' }}>
                      <span>Created by:</span>
                      <span className={'ml-1'}>{zone.createdBy}</span>
                    </div>

                    <div style={{ fontSize: '0.875rem' }}>
                      <span>Source:</span>
                      <span className={'ml-1 text-success'}>
                        {zone.source === null ? 'Imported' : zone.source}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      <div className={'row mt-3'}>
        <div className={'col-md-12'}>
          <Pager
            onPageChange={handlePageChange}
            total={total}
            take={
              props.take !== undefined
                ? props.take
                : DefaultGridSettings.pageSize
            }
            skip={props.skip !== undefined ? props.skip : 0}
            {...DefaultPagerSettings}
          />
        </div>
      </div>
    </div>
  );
};
